import { useMemo } from 'react'

import { useCurrentOrgId, useIsCloaking } from '~/store/slices/currentOrg'

import type { GridConnectionModel } from '../../client/ders'
import useManagedSites from './sites'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type GridConnectionFilters = {
  orgId?: string
  siteId?: string
  nodeId?: string
}

type UseManagedGridConnectionsInput = {
  filters?: GridConnectionFilters
}

export default function useManagedGridConnections({ filters }: UseManagedGridConnectionsInput = {}) {
  const { gridConnections } = useDataManager()
  const { data, isLoading, isFetching } = gridConnections

  // The manager fetches with `orgIds: [principalOrgId]` + a deep orgDepth,
  // so the cached set is already scoped to the principal user's org tree.
  // The site-join only does real narrowing when the caller wants a sub-slice
  // — i.e. while cloaking, or when an explicit `orgId` filter is passed.
  const isCloaking = useIsCloaking()
  const needsSiteJoin = isCloaking || filters?.orgId != null

  const {
    sites: orgSites,
    isLoading: isSitesLoading,
    isFetching: isSitesFetching
  } = useManagedSites({
    filters: { orgId: filters?.orgId }
  })

  return {
    gridConnections: useFilteredGridConnections(data, orgSites, needsSiteJoin, filters),
    isLoading: isLoading || (needsSiteJoin && isSitesLoading),
    isFetching: isFetching || (needsSiteJoin && isSitesFetching)
  }
}

function useFilteredGridConnections(
  data: GridConnectionModel[],
  orgSites: ReturnType<typeof useManagedSites>['sites'],
  needsSiteJoin: boolean,
  filters?: GridConnectionFilters
) {
  const currentOrgId = useCurrentOrgId()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  const allowedSiteIds = useMemo(() => new Set(orgSites.map((site) => site.depotId)), [orgSites])

  return useMemo(() => {
    const { siteId, nodeId } = normalizedFilters

    if (siteId === EMPTY || nodeId === EMPTY) {
      return []
    }

    let filtered = needsSiteJoin ? data.filter((gridConnection) => allowedSiteIds.has(gridConnection.siteId)) : data

    if (siteId != null) {
      filtered = filtered.filter((gridConnection) => gridConnection.siteId === siteId)
    }

    if (nodeId != null) {
      filtered = filtered.filter((gridConnection) => gridConnection.nodeId === nodeId)
    }

    return filtered
  }, [data, allowedSiteIds, needsSiteJoin, normalizedFilters])
}
